import { useEffect, useRef, useState } from "preact/hooks";
import { NarrativeEngine } from "@/narrative/NarrativeEngine";
import { RunManager, type RunSnapshot } from "@/roguelike/RunManager";
import type { CompileReport } from "@/story/compiler";

interface LogEntry {
  scene: string;
  text: string;
}

interface ChoiceView {
  index: number;
  text: string;
}

export function PreviewPlayer({ report }: { report: CompileReport }) {
  const engineRef = useRef<NarrativeEngine | null>(null);
  const runRef = useRef<RunManager | null>(null);
  const pendingFloorRef = useRef(false);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [choices, setChoices] = useState<ChoiceView[]>([]);
  const [done, setDone] = useState(false);
  const [toasts, setToasts] = useState<string[]>([]);
  const [runToken, setRunToken] = useState(0);

  const advance = (): void => {
    const engine = engineRef.current;
    if (!engine) return;

    const chunks: string[] = [];
    let scene = "…";

    while (engine.canContinue()) {
      const text = engine.continue();
      for (const tag of engine.getCurrentTags()) {
        const [kind, raw] = tag.split("::", 2);
        const value = (raw ?? "").trim();
        if (kind === "SCENE") scene = value;
        if (kind === "NEXT_FLOOR") pendingFloorRef.current = true;
        if (kind === "BOSS" || kind === "DIE" || kind === "GAME_OVER") {
          setToasts((prev) => [...prev, value]);
        }
      }
      if (text) chunks.push(text);
    }

    if (chunks.join("").trim().length > 0) {
      setLog((prev) => [...prev, { scene, text: chunks.join("\n\n") }]);
    }

    if (engine.isComplete()) {
      setDone(true);
      setChoices([]);
      return;
    }

    const needsFloor = pendingFloorRef.current;
    pendingFloorRef.current = false;

    if (needsFloor && runRef.current) {
      const floorNumber = Number(engine.getVariable("floor_number") ?? 1);
      const tier = runRef.current.tierForDepth(floorNumber);
      let floor: RunSnapshot;
      try {
        floor = runRef.current.pickFloor(floorNumber);
      } catch {
        setDone(true);
        return;
      }
      engine.setVariable("tier", tier);
      engine.choosePath(floor.path);
      advance();
      return;
    }

    setChoices(
      engine.getChoices().map((c) => ({ index: c.index, text: c.text }))
    );
    setDone(false);
  };

  useEffect(() => {
    engineRef.current = null;
    runRef.current = null;
    pendingFloorRef.current = false;
    setLog([]);
    setChoices([]);
    setToasts([]);
    setDone(false);

    if (!report.json) return;

    const engine = new NarrativeEngine();
    engine.loadStory(report.json);
    engineRef.current = engine;
    runRef.current =
      report.floors.length > 0 ? new RunManager(report.floors) : null;
    advance();
  }, [report, runToken]);

  const choose = (index: number): void => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.chooseChoice(index);
    setChoices([]);
    advance();
  };

  const restart = (): void => {
    setRunToken((t) => t + 1);
  };

  if (!report.json) {
    return (
      <div className="preview empty">
        Fix compile errors to preview the story here.
      </div>
    );
  }

  return (
    <div className="preview">
      <div className="preview-bar">
        <span>Live preview</span>
        {toasts.length > 0 ? (
          <span className="preview-toasts">
            ⚠ {toasts.slice(-3).join(" · ")}
          </span>
        ) : null}
        {done ? <span className="badge badge-published">THE END</span> : null}
      </div>
      <div className="preview-body">
        {log.map((entry, i) => (
          <div className="preview-node" key={i}>
            <span className="preview-scene">{entry.scene}</span>
            <span className="preview-text">{entry.text}</span>
          </div>
        ))}
        {done ? (
          <button className="choice-button" onClick={restart}>
            ↻ Restart preview
          </button>
        ) : (
          choices.map((choice) => (
            <button
              className="choice-button"
              key={choice.index}
              onClick={() => choose(choice.index)}
            >
              {choice.text}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
